import React, { useState, useEffect, useRef } from 'react';
import { useAsset } from '../../context/AssetContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Skeleton } from '../ui/skeleton';
import { ScrollArea } from '../ui/scroll-area';
import { Separator } from '../ui/separator';
import { toast } from 'sonner';
import {
  ArrowLeft, Pencil, UserPlus, UserMinus, Trash2, Loader2,
  Monitor, Hash, FileText, Calendar, ShieldCheck, ShieldAlert,
  Cpu, MemoryStick, HardDrive, Key, IndianRupee, User as UserIcon,
  History, ExternalLink, Mail, Briefcase,
  Upload, Link2, Eye, EyeOff, Search, Receipt, Lock
} from 'lucide-react';

const formatPrice = (price) => {
  if (!price && price !== 0) return '-';
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(price);
};

const formatDate = (d) => {
  if (!d) return '-';
  try { return new Date(d).toLocaleDateString(); } catch { return d; }
};

const formatDateTime = (d) => {
  if (!d) return '-';
  try { return new Date(d).toLocaleString(); } catch { return d; }
};

const formatAge = (months, years) => {
  if (months === null || months === undefined) return '-';
  if (months < 12) return `${months} month${months !== 1 ? 's' : ''}`;
  const remM = months % 12;
  return remM > 0 ? `${years}y ${remM}m` : `${years}y`;
};

const InfoRow = ({ icon: Icon, label, value, mono = false }) => (
  <div className="flex items-start gap-3 py-2.5">
    {Icon && <Icon className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />}
    <div className="flex-1 min-w-0">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`text-sm ${mono ? 'font-mono' : 'font-medium'} break-words`}>{value || '-'}</p>
    </div>
  </div>
);

const AssetDetail = ({ assetId, onBack, onViewEmployee }) => {
  const {
    lovs, employees, canRevealBitlocker,
    getAsset, updateAsset, deleteAsset,
    allocateAsset, unallocateAsset, getAssetHistory, getAssetAllocationHistory,
    fetchEmployees, getEmployee, getEmployeeAllocatedAssets,
    getAssetInvoice, listInvoices, uploadAssetInvoice, linkAssetInvoice, unlinkAssetInvoice, downloadAssetInvoice,
    revealAssetBitlockerKey
  } = useAsset();

  const [asset, setAsset] = useState(null);
  const [loading, setLoading] = useState(true);
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [allocHistoryData, setAllocHistoryData] = useState([]);
  const [allocHistoryLoading, setAllocHistoryLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  // Action dialogs
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [allocateDialogOpen, setAllocateDialogOpen] = useState(false);
  const [unallocateDialogOpen, setUnallocateDialogOpen] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [allocateEmployeeId, setAllocateEmployeeId] = useState('');
  const [formData, setFormData] = useState({});

  // Employee quick view dialog
  const [empDialogOpen, setEmpDialogOpen] = useState(false);
  const [empLoading, setEmpLoading] = useState(false);
  const [empData, setEmpData] = useState(null);
  const [empAssets, setEmpAssets] = useState([]);

  // Invoice state + dialogs
  const [invoiceMeta, setInvoiceMeta] = useState(null); // { has_invoice, invoice_no, invoice: {...} }
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [uploadInvoiceOpen, setUploadInvoiceOpen] = useState(false);

  // BitLocker reveal
  const [bitlockerRevealed, setBitlockerRevealed] = useState(null); // actual key string when revealed
  const [bitlockerLoading, setBitlockerLoading] = useState(false);
  const [linkInvoiceOpen, setLinkInvoiceOpen] = useState(false);
  const [unlinkInvoiceOpen, setUnlinkInvoiceOpen] = useState(false);
  const [uploadInvoiceNo, setUploadInvoiceNo] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadReplace, setUploadReplace] = useState(false);
  const [invoicesList, setInvoicesList] = useState([]);
  const [invoicesListLoading, setInvoicesListLoading] = useState(false);
  const [invoicePickerSearch, setInvoicePickerSearch] = useState('');
  const invoiceFileInputRef = useRef(null);

  const loadInvoice = async () => {
    setInvoiceLoading(true);
    const data = await getAssetInvoice(assetId);
    setInvoiceMeta(data);
    setInvoiceLoading(false);
  };

  const loadAsset = async () => {
    setLoading(true);
    const data = await getAsset(assetId);
    setAsset(data);
    setBitlockerRevealed(null);
    setLoading(false);
  };

  const loadHistory = async () => {
    setHistoryLoading(true);
    const data = await getAssetHistory(assetId);
    setHistoryData(data);
    setHistoryLoading(false);
  };

  const loadAllocHistory = async () => {
    setAllocHistoryLoading(true);
    const data = await getAssetAllocationHistory(assetId);
    setAllocHistoryData(data);
    setAllocHistoryLoading(false);
  };

  useEffect(() => {
    if (assetId) {
      loadAsset();
      loadHistory();
      loadAllocHistory();
      loadInvoice();
      fetchEmployees();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetId]);

  // Invoice handlers
  const openUploadDialog = () => {
    setUploadInvoiceNo(asset?.invoice_no || '');
    setUploadFile(null);
    setUploadReplace(false);
    setUploadInvoiceOpen(true);
  };

  const handleUploadInvoice = async () => {
    if (!uploadInvoiceNo?.trim()) {
      toast.error('Invoice No is required');
      return;
    }
    if (!uploadFile) {
      toast.error('Please select a PDF file');
      return;
    }
    setFormLoading(true);
    const result = await uploadAssetInvoice(assetId, uploadInvoiceNo.trim(), uploadFile, uploadReplace);
    setFormLoading(false);
    if (result.success) {
      toast.success('Invoice uploaded successfully');
      setUploadInvoiceOpen(false);
      setUploadFile(null);
      loadAsset();
      loadInvoice();
      loadHistory();
      loadAllocHistory();
    } else if (result.status === 409) {
      toast.warning('An invoice with this number already exists.');
      // Surface inline guidance: show replace option
      setUploadReplace(true);
    } else {
      toast.error(result.error);
    }
  };

  const openLinkDialog = async () => {
    setLinkInvoiceOpen(true);
    setInvoicePickerSearch('');
    setInvoicesListLoading(true);
    const list = await listInvoices('');
    setInvoicesList(list);
    setInvoicesListLoading(false);
  };

  const refreshInvoicePicker = async (q = '') => {
    setInvoicesListLoading(true);
    const list = await listInvoices(q);
    setInvoicesList(list);
    setInvoicesListLoading(false);
  };

  const handleLinkInvoice = async (invoiceNo) => {
    setFormLoading(true);
    const result = await linkAssetInvoice(assetId, invoiceNo);
    setFormLoading(false);
    if (result.success) {
      toast.success(`Linked to invoice ${invoiceNo}`);
      setLinkInvoiceOpen(false);
      loadAsset();
      loadInvoice();
      loadHistory();
      loadAllocHistory();
    } else {
      toast.error(result.error);
    }
  };

  const handleUnlinkInvoice = async () => {
    setFormLoading(true);
    const result = await unlinkAssetInvoice(assetId);
    setFormLoading(false);
    if (result.success) {
      toast.success(result.data?.invoice_file_deleted
        ? 'Invoice unlinked. PDF removed (no other assets were linked).'
        : 'Invoice unlinked. PDF preserved (other assets still linked).');
      setUnlinkInvoiceOpen(false);
      loadAsset();
      loadInvoice();
      loadHistory();
      loadAllocHistory();
    } else {
      toast.error(result.error);
    }
  };

  const handleViewInvoice = async () => {
    const result = await downloadAssetInvoice(assetId);
    if (!result.success) toast.error(result.error);
  };

  const formatBytes = (bytes) => {
    if (!bytes && bytes !== 0) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
  };

  const openEditDialog = () => {
    setFormData({
      asset_name: asset.asset_name || '',
      asset_type: asset.asset_type || '',
      status: asset.status || 'Available',
      serial_no: asset.serial_no || '',
      invoice_no: asset.invoice_no || '',
      invoice_date: asset.invoice_date || '',
      warranty_expiration_date: asset.warranty_expiration_date || '',
      adp_expiration_date: asset.adp_expiration_date || '',
      processor: asset.processor || '',
      ram: asset.ram || '',
      ssd_size: asset.ssd_size || '',
      windows_activation_key: asset.windows_activation_key || '',
      // BitLocker — only set field when the role can see it; otherwise leave it absent so we don't send the mask back.
      bitlocker_key: canRevealBitlocker ? (asset.bitlocker_key && asset.bitlocker_key !== '••••••••-••••-••••-••••-••••••••••••' ? asset.bitlocker_key : '') : '',
      price: asset.price || ''
    });
    setEditDialogOpen(true);
  };

  const handleEdit = async () => {
    setFormLoading(true);
    // Build payload — drop bitlocker_key entirely if it's empty so we don't accidentally clear it
    const payload = {
      ...formData,
      price: formData.price ? parseFloat(formData.price) : null,
    };
    if (!payload.bitlocker_key) delete payload.bitlocker_key;
    const result = await updateAsset(asset.asset_id, payload);
    setFormLoading(false);
    if (result.success) {
      toast.success('Asset updated successfully');
      setEditDialogOpen(false);
      loadAsset();
      loadHistory();
      loadAllocHistory();
    } else {
      toast.error(result.error);
    }
  };

  const handleDelete = async () => {
    setFormLoading(true);
    const result = await deleteAsset(asset.asset_id);
    setFormLoading(false);
    if (result.success) {
      toast.success('Asset deleted successfully');
      setDeleteDialogOpen(false);
      onBack?.();
    } else {
      toast.error(result.error);
    }
  };

  const handleAllocate = async () => {
    if (!allocateEmployeeId) {
      toast.error('Please select an employee');
      return;
    }
    setFormLoading(true);
    const result = await allocateAsset(asset.asset_id, allocateEmployeeId);
    setFormLoading(false);
    if (result.success) {
      toast.success('Asset allocated successfully');
      setAllocateDialogOpen(false);
      setAllocateEmployeeId('');
      loadAsset();
      loadHistory();
      loadAllocHistory();
    } else {
      toast.error(result.error);
    }
  };

  const handleUnallocate = async () => {
    setFormLoading(true);
    const result = await unallocateAsset(asset.asset_id);
    setFormLoading(false);
    if (result.success) {
      toast.success('Asset unallocated successfully');
      setUnallocateDialogOpen(false);
      loadAsset();
      loadHistory();
      loadAllocHistory();
    } else {
      toast.error(result.error);
    }
  };

  const openEmployeeDialog = async (employeeId) => {
    if (!employeeId) return;
    // If parent provided a navigation handler (full Employee Detail page), use it instead.
    if (onViewEmployee) {
      onViewEmployee(employeeId);
      return;
    }
    setEmpDialogOpen(true);
    setEmpLoading(true);
    setEmpData(null);
    setEmpAssets([]);
    const [emp, allocated] = await Promise.all([
      getEmployee(employeeId),
      getEmployeeAllocatedAssets(employeeId)
    ]);
    setEmpData(emp);
    setEmpAssets(allocated?.assets || []);
    setEmpLoading(false);
  };

  const handleRevealBitlocker = async () => {
    setBitlockerLoading(true);
    const res = await revealAssetBitlockerKey(asset.asset_id);
    setBitlockerLoading(false);
    if (res.success && res.data?.bitlocker_key) {
      setBitlockerRevealed(res.data.bitlocker_key);
      toast.success('BitLocker key revealed — this action is audit-logged');
    } else if (res.success) {
      toast.info('No BitLocker key is set for this asset');
    } else {
      toast.error(res.error);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4" data-testid="asset-detail-loading">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!asset) {
    return (
      <div className="text-center py-12" data-testid="asset-detail-notfound">
        <p className="text-muted-foreground mb-4">Asset not found.</p>
        <Button variant="outline" onClick={onBack}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Assets
        </Button>
      </div>
    );
  }

  const isAllocated = !!asset.allocated_to_id;
  const showTechSpecs = asset.asset_type === 'LAPTOP' || asset.asset_type === 'DESKTOP';
  const eligibleEmployees = Array.isArray(employees) ? employees.filter(e => e.employee_status === 'Employed') : [];

  return (
    <div className="space-y-6" data-testid="asset-detail">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex items-start gap-4">
          <Button variant="ghost" size="icon" onClick={onBack} data-testid="asset-detail-back-btn">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <h2 className="text-2xl font-bold tracking-tight" data-testid="asset-detail-name">{asset.asset_name}</h2>
              <Badge variant="outline" className="font-mono">{asset.asset_id}</Badge>
              <Badge variant="outline">{asset.asset_type}</Badge>
              <Badge variant={asset.status === 'Available' ? 'default' : asset.status === 'Allocated' ? 'secondary' : 'outline'}>
                {asset.status}
              </Badge>
            </div>
            {isAllocated && (
              <p className="text-sm text-muted-foreground flex items-center gap-1.5">
                <UserIcon className="h-3.5 w-3.5" />
                Allocated to{' '}
                <button
                  type="button"
                  className="text-primary hover:underline font-medium"
                  onClick={() => openEmployeeDialog(asset.allocated_to_id)}
                  data-testid="asset-detail-allocated-employee-link"
                >
                  {asset.allocated_to}
                </button>
              </p>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={openEditDialog} data-testid="asset-detail-edit-btn">
            <Pencil className="mr-2 h-4 w-4" /> Edit
          </Button>
          {!isAllocated ? (
            <Button variant="outline" onClick={() => setAllocateDialogOpen(true)} data-testid="asset-detail-allocate-btn">
              <UserPlus className="mr-2 h-4 w-4" /> Allocate
            </Button>
          ) : (
            <Button variant="outline" onClick={() => setUnallocateDialogOpen(true)} data-testid="asset-detail-unallocate-btn">
              <UserMinus className="mr-2 h-4 w-4" /> Unallocate
            </Button>
          )}
          <Button
            variant="destructive"
            onClick={() => setDeleteDialogOpen(true)}
            disabled={isAllocated}
            title={isAllocated ? 'Unallocate before deleting' : 'Delete asset'}
            data-testid="asset-detail-delete-btn"
          >
            <Trash2 className="mr-2 h-4 w-4" /> Delete
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview" data-testid="asset-detail-tab-overview">
            <Monitor className="h-4 w-4 mr-2" /> Overview
          </TabsTrigger>
          <TabsTrigger value="allocation" data-testid="asset-detail-tab-allocation">
            <UserIcon className="h-4 w-4 mr-2" /> Allocation History
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="asset-detail-tab-history">
            <History className="h-4 w-4 mr-2" /> Asset Update History
          </TabsTrigger>
        </TabsList>

        {/* Overview */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Identification</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <InfoRow icon={Hash} label="Asset ID" value={asset.asset_id} mono />
                <Separator />
                <InfoRow icon={Monitor} label="Asset Name" value={asset.asset_name} />
                <Separator />
                <InfoRow icon={FileText} label="Asset Type" value={asset.asset_type} />
                <Separator />
                <InfoRow icon={Hash} label="Serial Number" value={asset.serial_no} mono />
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Purchase & Warranty</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <InfoRow icon={FileText} label="Invoice No" value={asset.invoice_no} mono />
                <Separator />
                <InfoRow icon={Calendar} label="Invoice Date" value={formatDate(asset.invoice_date)} />
                <Separator />
                {/* Invoice File */}
                <div className="flex items-start gap-3 py-2.5" data-testid="asset-detail-invoice-row">
                  <Receipt className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-muted-foreground">Invoice File (PDF)</p>
                    {invoiceLoading ? (
                      <Skeleton className="h-5 w-32 mt-1" />
                    ) : invoiceMeta?.has_invoice ? (
                      <div className="space-y-1.5 mt-0.5">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium break-all" data-testid="asset-detail-invoice-filename">
                            {invoiceMeta.invoice?.filename}
                          </span>
                          <Badge variant="secondary" className="text-[10px]">
                            {formatBytes(invoiceMeta.invoice?.size)}
                          </Badge>
                          {invoiceMeta.invoice?.linked_asset_count > 1 && (
                            <Badge variant="outline" className="text-[10px]" data-testid="asset-detail-invoice-shared-badge">
                              shared with {invoiceMeta.invoice.linked_asset_count - 1} other{invoiceMeta.invoice.linked_asset_count - 1 === 1 ? '' : 's'}
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <Button size="sm" variant="outline" className="h-7" onClick={handleViewInvoice} data-testid="asset-detail-invoice-view-btn">
                            <Eye className="mr-1.5 h-3.5 w-3.5" /> View
                          </Button>
                          <Button size="sm" variant="outline" className="h-7" onClick={openUploadDialog} data-testid="asset-detail-invoice-replace-btn">
                            <Upload className="mr-1.5 h-3.5 w-3.5" /> Replace
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 text-destructive hover:text-destructive" onClick={() => setUnlinkInvoiceOpen(true)} data-testid="asset-detail-invoice-unlink-btn">
                            <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Unlink
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-1.5 mt-0.5">
                        <p className="text-sm text-muted-foreground italic">Not uploaded</p>
                        <div className="flex items-center gap-2 flex-wrap">
                          <Button size="sm" variant="outline" className="h-7" onClick={openUploadDialog} data-testid="asset-detail-invoice-upload-btn">
                            <Upload className="mr-1.5 h-3.5 w-3.5" /> Upload PDF
                          </Button>
                          <Button size="sm" variant="outline" className="h-7" onClick={openLinkDialog} data-testid="asset-detail-invoice-link-btn">
                            <Link2 className="mr-1.5 h-3.5 w-3.5" /> Link Existing
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <Separator />
                <InfoRow icon={ShieldCheck} label="Warranty Expiration" value={formatDate(asset.warranty_expiration_date)} />
                <Separator />
                <InfoRow icon={ShieldAlert} label="ADP Expiration" value={formatDate(asset.adp_expiration_date)} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Financials & Age</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <InfoRow icon={IndianRupee} label="Price" value={formatPrice(asset.price)} mono />
                <Separator />
                <InfoRow icon={Calendar} label="Age" value={formatAge(asset.age_months, asset.age_years)} />
                <Separator />
                <InfoRow
                  icon={UserIcon}
                  label="Allocated To"
                  value={asset.allocated_to ? `${asset.allocated_to} (${asset.allocated_to_id})` : 'Not allocated'}
                />
              </CardContent>
            </Card>

            {showTechSpecs && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Technical Specifications</CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <InfoRow icon={Cpu} label="Processor" value={asset.processor} />
                  <Separator />
                  <InfoRow icon={MemoryStick} label="RAM" value={asset.ram} />
                  <Separator />
                  <InfoRow icon={HardDrive} label="SSD Size" value={asset.ssd_size} />
                  <Separator />
                  <InfoRow icon={Key} label="Windows Activation Key" value={asset.windows_activation_key} mono />
                  <Separator />
                  {/* BitLocker Recovery Key — only shows for LAPTOP/DESKTOP */}
                  <div className="flex items-start gap-3 py-2.5" data-testid="asset-detail-bitlocker-row">
                    <Lock className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-muted-foreground">BitLocker Recovery Key</p>
                      {!asset.bitlocker_key_set ? (
                        <p className="text-sm text-muted-foreground italic">Not set</p>
                      ) : bitlockerRevealed ? (
                        <div className="flex items-center gap-2 flex-wrap mt-0.5">
                          <code className="text-sm font-mono break-all bg-muted px-2 py-1 rounded" data-testid="asset-detail-bitlocker-value">
                            {bitlockerRevealed}
                          </code>
                          <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => setBitlockerRevealed(null)} data-testid="asset-detail-bitlocker-hide-btn">
                            <EyeOff className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 flex-wrap mt-0.5">
                          <code className="text-sm font-mono break-all bg-muted px-2 py-1 rounded text-muted-foreground" data-testid="asset-detail-bitlocker-masked">
                            ••••••••-••••-••••-••••-••••••••••••
                          </code>
                          {canRevealBitlocker ? (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 px-2"
                              onClick={handleRevealBitlocker}
                              disabled={bitlockerLoading}
                              data-testid="asset-detail-bitlocker-reveal-btn"
                            >
                              {bitlockerLoading
                                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                : <><Eye className="h-3.5 w-3.5 mr-1" /> Reveal</>}
                            </Button>
                          ) : (
                            <Badge variant="outline" className="text-[10px]" data-testid="asset-detail-bitlocker-no-permission">
                              Requires IT Admin
                            </Badge>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Allocation History (allocations only) */}
        <TabsContent value="allocation">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Employees Who Used This Asset</CardTitle>
            </CardHeader>
            <CardContent>
              {allocHistoryLoading ? (
                <div className="space-y-2">
                  {[1, 2, 3].map(i => <Skeleton key={i} className="h-14 w-full" />)}
                </div>
              ) : allocHistoryData.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground" data-testid="asset-detail-alloc-history-empty">
                  This asset has never been allocated.
                </div>
              ) : (
                <ScrollArea className="max-h-[500px] pr-4">
                  <div className="space-y-2" data-testid="asset-detail-alloc-history-list">
                    {allocHistoryData.map((row, idx) => (
                      <div
                        key={idx}
                        className="border rounded-lg p-3 flex items-center justify-between flex-wrap gap-3"
                        data-testid={`asset-detail-alloc-history-row-${idx}`}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <UserIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                          {row.employee_id ? (
                            <button
                              type="button"
                              className="text-sm font-medium text-primary hover:underline text-left"
                              onClick={() => openEmployeeDialog(row.employee_id)}
                              data-testid={`asset-detail-alloc-history-employee-link-${idx}`}
                            >
                              {row.employee_name || row.employee_id}
                            </button>
                          ) : (
                            <span className="text-sm font-medium text-muted-foreground italic">Unknown employee</span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 flex-wrap text-xs text-muted-foreground">
                          <span className="inline-flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            Allocated: {row.allocated_at ? formatDateTime(row.allocated_at) : '—'}
                          </span>
                          <Badge
                            variant={row.status === 'Active' ? 'default' : 'outline'}
                            data-testid={`asset-detail-alloc-history-status-${idx}`}
                          >
                            {row.status}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Asset Update History (full audit log) */}
        <TabsContent value="history">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Lifecycle Timeline</CardTitle>
            </CardHeader>
            <CardContent>
              {historyLoading ? (
                <div className="space-y-2">
                  {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-16 w-full" />)}
                </div>
              ) : historyData.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground" data-testid="asset-detail-history-empty">
                  No history found for this asset.
                </div>
              ) : (
                <ScrollArea className="max-h-[500px] pr-4">
                  <div className="space-y-3" data-testid="asset-detail-history-list">
                    {historyData.map((log, idx) => (
                      <div key={idx} className="border rounded-lg p-3" data-testid={`asset-history-row-${idx}`}>
                        <div className="flex items-center justify-between mb-1.5 flex-wrap gap-2">
                          <Badge
                            variant={
                              log.action?.includes('CREATED') ? 'default' :
                                log.action?.includes('ALLOCATED') ? 'secondary' :
                                  log.action?.includes('UNALLOCATED') ? 'outline' :
                                    log.action?.includes('UPDATED') ? 'default' : 'outline'
                            }
                          >
                            {log.action}
                          </Badge>
                          <span className="text-xs text-muted-foreground">{formatDateTime(log.timestamp)}</span>
                        </div>
                        <p className="text-sm">{log.details}</p>
                        <div className="flex items-center justify-between mt-2 flex-wrap gap-2">
                          <p className="text-xs text-muted-foreground">By: {log.username}</p>
                          {log.target_employee_id && (
                            <button
                              type="button"
                              className="text-xs text-primary hover:underline inline-flex items-center gap-1"
                              onClick={() => openEmployeeDialog(log.target_employee_id)}
                              data-testid={`asset-history-employee-link-${idx}`}
                            >
                              <UserIcon className="h-3 w-3" />
                              View {log.target_employee_name || log.target_employee_id}
                              <ExternalLink className="h-3 w-3" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Asset</DialogTitle>
            <DialogDescription>Update the asset details below</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Asset ID</Label>
                <Input value={asset.asset_id} disabled />
              </div>
              <div className="space-y-2">
                <Label>Asset Name</Label>
                <Input value={formData.asset_name} onChange={(e) => setFormData({ ...formData, asset_name: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Asset Type</Label>
                <Select value={formData.asset_type} onValueChange={(v) => setFormData({ ...formData, asset_type: v })}>
                  <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    {lovs.asset_types.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                  <SelectTrigger><SelectValue placeholder="Select status" /></SelectTrigger>
                  <SelectContent>
                    {lovs.asset_statuses.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Serial Number</Label>
                <Input value={formData.serial_no} onChange={(e) => setFormData({ ...formData, serial_no: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Price (₹)</Label>
                <Input type="number" value={formData.price} onChange={(e) => setFormData({ ...formData, price: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Invoice Number</Label>
                <Input value={formData.invoice_no} onChange={(e) => setFormData({ ...formData, invoice_no: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Invoice Date</Label>
                <Input type="date" value={formData.invoice_date} onChange={(e) => setFormData({ ...formData, invoice_date: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Warranty Expiration</Label>
                <Input type="date" value={formData.warranty_expiration_date} onChange={(e) => setFormData({ ...formData, warranty_expiration_date: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>ADP Expiration</Label>
                <Input type="date" value={formData.adp_expiration_date} onChange={(e) => setFormData({ ...formData, adp_expiration_date: e.target.value })} />
              </div>
            </div>
            {showTechSpecs && (
              <div className="border-t pt-4 mt-2">
                <h4 className="font-medium mb-4">Technical Specifications</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Processor</Label>
                    <Input value={formData.processor} onChange={(e) => setFormData({ ...formData, processor: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label>RAM</Label>
                    <Input value={formData.ram} onChange={(e) => setFormData({ ...formData, ram: e.target.value })} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div className="space-y-2">
                    <Label>SSD Size</Label>
                    <Input value={formData.ssd_size} onChange={(e) => setFormData({ ...formData, ssd_size: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label>Windows Activation Key</Label>
                    <Input value={formData.windows_activation_key} onChange={(e) => setFormData({ ...formData, windows_activation_key: e.target.value })} />
                  </div>
                </div>
                {canRevealBitlocker && (
                  <div className="grid grid-cols-1 gap-4 mt-4">
                    <div className="space-y-2">
                      <Label className="flex items-center gap-1.5">
                        <Lock className="h-3.5 w-3.5" />
                        BitLocker Recovery Key
                        <span className="text-xs text-muted-foreground font-normal">(optional)</span>
                      </Label>
                      <Input
                        value={formData.bitlocker_key || ''}
                        onChange={(e) => setFormData({ ...formData, bitlocker_key: e.target.value })}
                        placeholder="e.g., 123456-789012-345678-901234-567890-123456-789012-345678"
                        className="font-mono text-xs"
                        data-testid="asset-detail-edit-bitlocker"
                      />
                      <p className="text-[11px] text-muted-foreground">
                        Leave unchanged to keep the existing key. Visible only to Super Admin / IT Admin.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleEdit} disabled={formLoading} data-testid="asset-detail-edit-save-btn">
              {formLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Allocate Dialog */}
      <Dialog open={allocateDialogOpen} onOpenChange={setAllocateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Allocate Asset</DialogTitle>
            <DialogDescription>Assign this asset to an employee</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-4">
            <Label>Employee</Label>
            <Select value={allocateEmployeeId} onValueChange={setAllocateEmployeeId}>
              <SelectTrigger data-testid="asset-detail-allocate-employee-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
              <SelectContent>
                {eligibleEmployees.map(e => (
                  <SelectItem key={e.employee_id} value={e.employee_id}>
                    {e.employee_name} ({e.employee_id})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAllocateDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAllocate} disabled={formLoading} data-testid="asset-detail-allocate-confirm-btn">
              {formLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Allocate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unallocate Confirm */}
      <Dialog open={unallocateDialogOpen} onOpenChange={setUnallocateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Unallocate Asset</DialogTitle>
            <DialogDescription>
              Remove allocation from <span className="font-medium">{asset.allocated_to}</span>?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUnallocateDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleUnallocate} disabled={formLoading} data-testid="asset-detail-unallocate-confirm-btn">
              {formLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Unallocate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Asset</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{asset.asset_name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={formLoading} data-testid="asset-detail-delete-confirm-btn">
              {formLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Upload Invoice Dialog */}
      <Dialog open={uploadInvoiceOpen} onOpenChange={setUploadInvoiceOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{invoiceMeta?.has_invoice ? 'Replace Invoice PDF' : 'Upload Invoice PDF'}</DialogTitle>
            <DialogDescription>
              Upload a PDF for this asset's invoice. If the same invoice number is used for several assets, you can later link them instead of re-uploading.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="space-y-2">
              <Label>Invoice No *</Label>
              <Input
                value={uploadInvoiceNo}
                onChange={(e) => setUploadInvoiceNo(e.target.value)}
                placeholder="e.g., INV-2025-001"
                data-testid="asset-invoice-upload-no"
              />
              <p className="text-xs text-muted-foreground">This invoice number identifies the shared PDF in the registry.</p>
            </div>
            <div className="space-y-2">
              <Label>PDF File *</Label>
              <input
                ref={invoiceFileInputRef}
                type="file"
                accept="application/pdf,.pdf"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                className="block w-full text-sm file:mr-3 file:py-1.5 file:px-3 file:rounded file:border file:border-input file:bg-background file:text-foreground file:cursor-pointer"
                data-testid="asset-invoice-upload-file"
              />
              {uploadFile && (
                <p className="text-xs text-muted-foreground">
                  Selected: <span className="font-medium">{uploadFile.name}</span> ({formatBytes(uploadFile.size)})
                </p>
              )}
            </div>
            {uploadReplace && (
              <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                A PDF already exists for this invoice number. Proceeding will <strong>replace</strong> the file for all assets currently linked to it.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadInvoiceOpen(false)}>Cancel</Button>
            <Button onClick={handleUploadInvoice} disabled={formLoading} data-testid="asset-invoice-upload-confirm-btn">
              {formLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {uploadReplace ? 'Replace PDF' : 'Upload'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link Existing Invoice Dialog */}
      <Dialog open={linkInvoiceOpen} onOpenChange={setLinkInvoiceOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Link to an Existing Invoice</DialogTitle>
            <DialogDescription>
              Reuse a previously uploaded invoice PDF. Pick the invoice number this asset belongs to.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search invoice no or filename..."
                value={invoicePickerSearch}
                onChange={(e) => { setInvoicePickerSearch(e.target.value); refreshInvoicePicker(e.target.value); }}
                className="pl-10"
                data-testid="asset-invoice-picker-search"
              />
            </div>
            <ScrollArea className="h-[360px] pr-3">
              {invoicesListLoading ? (
                <div className="space-y-2">
                  {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-14 w-full" />)}
                </div>
              ) : invoicesList.length === 0 ? (
                <div className="text-center py-10 text-muted-foreground text-sm" data-testid="asset-invoice-picker-empty">
                  No registered invoices yet. Upload one for this asset first to seed the registry.
                </div>
              ) : (
                <div className="space-y-2" data-testid="asset-invoice-picker-list">
                  {invoicesList.map((inv) => (
                    <button
                      key={inv.invoice_no}
                      type="button"
                      onClick={() => handleLinkInvoice(inv.invoice_no)}
                      disabled={formLoading}
                      className="w-full text-left border rounded-lg p-3 hover:bg-muted/50 transition-colors disabled:opacity-50"
                      data-testid={`asset-invoice-picker-item-${inv.invoice_no}`}
                    >
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="min-w-0">
                          <p className="text-sm font-mono font-medium">{inv.invoice_no}</p>
                          <p className="text-xs text-muted-foreground break-all">{inv.filename}</p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <Badge variant="secondary" className="text-[10px]">{formatBytes(inv.size)}</Badge>
                          <Badge variant="outline" className="text-[10px]">
                            {inv.linked_asset_count} asset{inv.linked_asset_count === 1 ? '' : 's'}
                          </Badge>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkInvoiceOpen(false)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unlink Invoice Confirm */}
      <Dialog open={unlinkInvoiceOpen} onOpenChange={setUnlinkInvoiceOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Unlink Invoice</DialogTitle>
            <DialogDescription>
              {invoiceMeta?.invoice?.linked_asset_count > 1
                ? `This invoice is shared with ${invoiceMeta.invoice.linked_asset_count - 1} other asset(s). The PDF will remain available in the registry.`
                : 'No other assets are linked. The invoice PDF will be removed from the registry after unlinking.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUnlinkInvoiceOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleUnlinkInvoice} disabled={formLoading} data-testid="asset-invoice-unlink-confirm-btn">
              {formLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Unlink
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Employee Quick View Dialog */}
      <Dialog open={empDialogOpen} onOpenChange={setEmpDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Employee Details</DialogTitle>
            <DialogDescription>Profile and allocated assets</DialogDescription>
          </DialogHeader>

          {empLoading ? (
            <div className="space-y-2 py-4">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-32 w-full" />
            </div>
          ) : !empData ? (
            <div className="text-center py-8 text-muted-foreground">Employee not found.</div>
          ) : (
            <div className="space-y-4" data-testid="employee-quick-view">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <h3 className="text-lg font-semibold" data-testid="employee-quick-view-name">{empData.employee_name}</h3>
                    <Badge variant="outline" className="font-mono">{empData.employee_id}</Badge>
                    <Badge variant={empData.employee_status === 'Employed' ? 'default' : 'outline'}>
                      {empData.employee_status}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <span>{empData.email || '-'}</span>
                    </div>
                    {empData.project_name && (
                      <div className="flex items-center gap-2">
                        <Briefcase className="h-4 w-4 text-muted-foreground" />
                        <span>Project: {empData.project_name}</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Currently Allocated Assets ({empAssets.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  {empAssets.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">No assets currently allocated.</p>
                  ) : (
                    <div className="space-y-2">
                      {empAssets.map((a) => (
                        <div key={a.asset_id} className="flex items-center justify-between p-2 border rounded-md text-sm">
                          <div>
                            <span className="font-mono text-xs">{a.asset_id}</span>
                            <span className="mx-2">•</span>
                            <span className="font-medium">{a.asset_name}</span>
                          </div>
                          <Badge variant="outline" className="text-xs">{a.asset_type}</Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEmpDialogOpen(false)} data-testid="employee-quick-view-close">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AssetDetail;
